import React, { useState } from "react";
import { Box, Button, Heading, HStack, VStack, useDisclosure, useToast, Text } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Plus } from "lucide-react";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { AddIPModal } from "./components/AddIPModal";
import { IPTag } from "./components/IPTag";
import {
  useAddIPMutation,
  useDeleteIPAddressMutation,
  useEditIPAddressMutation,
  useGetWhitelistedIpsQuery,
  useSyncMyIPMutation,
} from "@state/apiSlice/requests";
import { IPAddress } from "@hz/stealthy-gmc-cloaker-common/api-types/whitelist";
import { roles } from "@hz/stealthy-gmc-cloaker-common/constants";
import { useSelector } from "react-redux";
import { selectUserState } from "@state/userSlice/userSlice";

interface WhitelistedIP {
  id: string;
  address: string;
  label?: string;
}
export const WhitelistIPs = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { userId} = useSelector(selectUserState);

  const { data, isLoading } = useGetWhitelistedIpsQuery({});
  const [addIPAddress] = useAddIPMutation();
  const [editIPAddress] = useEditIPAddressMutation();
  const [deleteIPAddress] = useDeleteIPAddressMutation();
  const [syncMyIP] = useSyncMyIPMutation();

  const handleDelete = async (ipId: string) => {
    try {
      const result = await deleteIPAddress({ ipId }).unwrap();

      if (result.success) {
        toast({
          title: "IP address deleted",
          status: "success",
          duration: 3000,
          position: "bottom-right",
        });
        onClose();
      }
    } catch (err) {
      toast({
        title: "Error deleting IP Address",
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    }
  };

  const handleEdit = async ({ ipId, ipAddress, tag }: Pick<IPAddress, "ipId" | "ipAddress" | "tag">) => {
    try {
      const result = await editIPAddress({ ipId, ipAddress, tag }).unwrap();

      if (result.success) {
        toast({
          title: "IP address added",
          status: "success",
          duration: 3000,
          position: "bottom-right",
        });
        onClose();
      }
    } catch (err) {
      toast({
        title: "Error adding IP Address",
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    }
  };

  const handleAdd = async (newIP: Omit<WhitelistedIP, "id">) => {
    try {
      const result = await addIPAddress({ ipAddress: newIP.address, tag: newIP.label }).unwrap();

      if (result.success) {
        toast({
          title: "IP address added",
          status: "success",
          duration: 3000,
          position: "bottom-right",
        });
        onClose();
      }
    } catch (err) {
      toast({
        title: "Error adding IP Address",
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncMyIP({}).unwrap();

      if (result.success) {
        toast({
          title: result.message,
          status: "info",
          duration: 2000,
          position: "bottom-right",
        });
      }
    } catch (err) {
      toast({
        title: "Error adding your IP Address",
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    }
  };

  const MotionBox = motion(Box);
  return (
    <DashboardLayout>
      <Box>
        <HStack justify="space-between" mb={8}>
          <Heading size="lg">Whitelist IPs</Heading>
          <HStack spacing={4}>
            <Button
              variant="outline"
              leftIcon={<RefreshCw size={18} />}
              onClick={handleSync}
              borderColor="gray.700"
              color="gray.400"
              _hover={{
                bg: "gray.700",
                color: "white",
              }}
            >
              Sync My IP
            </Button>
            <Button
              leftIcon={<Plus size={18} />}
              onClick={onOpen}
              bg="#ff3d00"
              color="white"
              _hover={{
                bg: "#e53700",
                boxShadow: "0 0 20px rgba(255, 61, 0, 0.5)",
              }}
            >
              Add IP
            </Button>
          </HStack>
        </HStack>

        {isLoading ? (
          <Text color="gray.400">Loading...</Text>
        ) : (
          <Box bg="gray.800" borderRadius="lg" border="1px" borderColor="gray.700" p={6}>
            <VStack align="stretch" spacing={4}>
              <AnimatePresence>
                {data?.ips.map((x) => (
                  <MotionBox
                    key={x.ipId}
                    initial={{
                      opacity: 0,
                      y: -10,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      x: -10,
                    }}
                    transition={{
                      duration: 0.2,
                    }}
                  >
                    <IPTag ip={x} onDelete={() => handleDelete(x.ipId)} handleEdit={handleEdit} myUserId={userId} />
                  </MotionBox>
                ))}

                {data?.ips.length === 0 && (
                  <VStack>
                    <Text color="gray.400">Whitelist your ip</Text>

                    <Button
                      variant="outline"
                      leftIcon={<RefreshCw size={18} />}
                      onClick={handleSync}
                      borderColor="gray.700"
                      color="gray.400"
                      _hover={{
                        bg: "gray.700",
                        color: "white",
                      }}
                    >
                      Sync My IP
                    </Button>
                  </VStack>
                )}
              </AnimatePresence>
            </VStack>
          </Box>
        )}
      </Box>
      <AddIPModal isOpen={isOpen} onClose={onClose} onSubmit={handleAdd} />
    </DashboardLayout>
  );
};
